import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Modal } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { AiUploadImageSection } from './AiUploadImageSection'
import { AiUploadDocumentSection } from './AiUploadDocumentSection'
import { AiDataExtractionAnalyzingModal, ProcessingState } from './AiDataExtractionAnalyzingModal'
import { useAiExtractionFlow } from './useAiExtractionFlow'
import { useAiUploadedAttachments } from './useAiUploadedAttachments'
import { AiDataExtractionUploadError } from './AiDataExtractionUploadError'
import { useFeatureSwitch } from '../../../../utils/features/FeatureSwitchContext'
import { FeatureSwitch } from '../../../../utils/features/featureSwitches'
import { useAppSelector } from '../../../../app/hooks'
import { InitState } from '../../../../redux/reducers/root/initialState'
import {
  trackAiDataExtractionOverlayClick,
  trackAiDataExtractionOverlayPageView,
  trackAiDataExtractionProcessingSuccess,
} from './aiDataExtractionTracking'

interface AiDataExtractionUploadModalProps {
  extractionApiError?: boolean
  isOpen: boolean
  onClearExtractionApiError?: () => void
  onClose: () => void
  onExtractionFailed?: () => void
  onSuccess?: () => void
}

export const AiDataExtractionUploadModal = ({
  extractionApiError,
  isOpen,
  onClearExtractionApiError,
  onClose,
  onExtractionFailed,
  onSuccess,
}: AiDataExtractionUploadModalProps) => {
  const { t } = useTranslation()
  const { isActive: isAiDataExtractionReentryEnabled } = useFeatureSwitch(
    FeatureSwitch.PPA_12731_AI_DATA_EXTRACTION_REENTRY
  )
  const flowInformation = useAppSelector((state: InitState) => state.flowFeature.flowInformation)
  const entryMode = useAppSelector(
    (state: InitState) => state.insertionFeature.insertionIdentification.metaInformation?.entryMode
  )
  const isExtractionErrorControlled = extractionApiError !== undefined
  const [internalExtractionApiError, setInternalExtractionApiError] = useState(false)
  const showExtractionApiError = isExtractionErrorControlled ? extractionApiError : internalExtractionApiError
  const closeEventHandledRef = useRef(false)

  const clearExtractionApiError = useCallback(() => {
    if (isExtractionErrorControlled) {
      onClearExtractionApiError?.()
    } else {
      setInternalExtractionApiError(false)
    }
  }, [isExtractionErrorControlled, onClearExtractionApiError])

  const handleModalClose = useCallback(() => {
    if (!isExtractionErrorControlled) {
      setInternalExtractionApiError(false)
    }
    onClose()
  }, [isExtractionErrorControlled, onClose])

  const handleOverlayClose = useCallback(() => {
    if (closeEventHandledRef.current) {
      return
    }

    closeEventHandledRef.current = true
    trackAiDataExtractionOverlayClick('close_modal_click', flowInformation, entryMode)
    handleModalClose()

    window.setTimeout(() => {
      closeEventHandledRef.current = false
    }, 0)
  }, [entryMode, flowInformation, handleModalClose])

  const handleCancelClick = useCallback(() => {
    trackAiDataExtractionOverlayClick('cancel_click', flowInformation, entryMode)
    handleModalClose()
  }, [entryMode, flowInformation, handleModalClose])

  const {
    documentsMaxFileSizeErrorText,
    energyCertificateDocument,
    energyCertificateErrorText,
    floorPlanDocument,
    floorPlanErrorText,
    getLatestAttachmentsForExtraction,
    hasSelectedFiles,
    imageErrorText,
    images,
    onEnergyCertificateRemove,
    onEnergyCertificateSelect,
    onFloorPlanRemove,
    onFloorPlanSelect,
    onImageRemove,
    onImageReorder,
    onImageSelect,
    waitForPendingUploads,
  } = useAiUploadedAttachments()
  const { isAnalyzingOpen, processingState, retryAnalyzing, startAnalyzing } = useAiExtractionFlow({
    getLatestAttachmentsForExtraction,
    hasSelectedFiles,
    onClearExtractionApiError: clearExtractionApiError,
    onClose,
    onExtractionFailed: () => {
      if (isExtractionErrorControlled) {
        onExtractionFailed?.()
      } else {
        setInternalExtractionApiError(true)
      }
    },
    onProcessingSuccess: (attachments) => {
      trackAiDataExtractionProcessingSuccess(attachments, flowInformation, entryMode)
    },
    onSuccess,
    waitForPendingUploads,
  })

  useEffect(() => {
    if (!isOpen || isAnalyzingOpen) {
      return
    }

    trackAiDataExtractionOverlayPageView(flowInformation, entryMode)
  }, [entryMode, flowInformation, isAnalyzingOpen, isOpen])

  const handleUploadFilesClick = useCallback(() => {
    trackAiDataExtractionOverlayClick('upload_file_click', flowInformation, entryMode)
    startAnalyzing()
  }, [entryMode, flowInformation, startAnalyzing])

  const primaryFooterBtn = (
    <Modal.PrimaryFooterButton
      label={t(
        isAiDataExtractionReentryEnabled
          ? 'wizard.uploadAiBanner.modal.v2.uploadButton'
          : 'wizard.uploadAiBanner.modal.uploadButton'
      )}
      onClick={handleUploadFilesClick}
      disabled={!hasSelectedFiles}
      variant='filledDefault'
    />
  )
  const secondaryFooterBtn = (
    <Modal.SecondaryFooterButton label={t('wizard.uploadAiBanner.modal.cancelButton')} onClick={handleCancelClick} />
  )

  return (
    <>
      <Modal.Root isOpen={isOpen && !isAnalyzingOpen} onClose={handleOverlayClose}>
        <Modal.Backdrop>
          <Modal.Surface width={601}>
            <Modal.Header>
              <Modal.TitleGroup>
                <Modal.Title>
                  {t(
                    isAiDataExtractionReentryEnabled
                      ? 'wizard.uploadAiBanner.modal.v2.title'
                      : 'wizard.uploadAiBanner.modal.title'
                  )}
                </Modal.Title>
              </Modal.TitleGroup>
              <Modal.CloseButton aria-label='close' onClick={handleOverlayClose} />
            </Modal.Header>
            <Modal.Body>
              <p className='font-body-large-regular margin-bottom-l'>
                {t(
                  isAiDataExtractionReentryEnabled
                    ? 'wizard.uploadAiBanner.modal.v2.description'
                    : 'wizard.uploadAiBanner.modal.description'
                )}
              </p>
              {showExtractionApiError ? (
                <AiDataExtractionUploadError
                  className='margin-bottom-l'
                  data-testid='ai-extraction-api-error'
                  message={t('wizard.uploadAiBanner.modal.extractionApiError')}
                  role='alert'
                />
              ) : null}
              <AiUploadImageSection
                errorText={imageErrorText}
                images={images}
                onFileSelect={onImageSelect}
                onAddClick={() => trackAiDataExtractionOverlayClick('add_photos_click', flowInformation, entryMode)}
                onRemove={(imageId) => {
                  trackAiDataExtractionOverlayClick('remove_photo_click', flowInformation, entryMode)
                  onImageRemove(imageId)
                }}
                onReorder={onImageReorder}
              />
              <AiUploadDocumentSection
                documentsSectionErrorText={documentsMaxFileSizeErrorText}
                energyCertificateErrorText={energyCertificateErrorText}
                floorPlanDocument={floorPlanDocument}
                floorPlanErrorText={floorPlanErrorText}
                energyCertificateDocument={energyCertificateDocument}
                onFloorPlanSelect={onFloorPlanSelect}
                onEnergyCertificateSelect={onEnergyCertificateSelect}
                onFloorPlanAddClick={() =>
                  trackAiDataExtractionOverlayClick('add_floorplan_click', flowInformation, entryMode)
                }
                onEnergyCertificateAddClick={() =>
                  trackAiDataExtractionOverlayClick('add_energy_certificate_click', flowInformation, entryMode)
                }
                onFloorPlanRemove={() => {
                  trackAiDataExtractionOverlayClick('remove_floorplan_click', flowInformation, entryMode)
                  onFloorPlanRemove()
                }}
                onEnergyCertificateRemove={() => {
                  trackAiDataExtractionOverlayClick('remove_energy_certificate_click', flowInformation, entryMode)
                  onEnergyCertificateRemove()
                }}
              />
              <p className='font-body-small-regular margin-bottom-l margin-top-m font-light'>
                <span className={'font-body-small-bold'}>{t('wizard.uploadAiBanner.modal.hint')}</span>
                <span>
                  {t(
                    isAiDataExtractionReentryEnabled
                      ? 'wizard.uploadAiBanner.modal.v2.hintText'
                      : 'wizard.uploadAiBanner.modal.hintText'
                  )}
                </span>
              </p>
            </Modal.Body>
            <Modal.Footer>
              {secondaryFooterBtn}
              {primaryFooterBtn}
            </Modal.Footer>
          </Modal.Surface>
        </Modal.Backdrop>
      </Modal.Root>
      <AiDataExtractionAnalyzingModal
        visible={isAnalyzingOpen}
        state={processingState}
        onRetry={processingState === ProcessingState.Error ? retryAnalyzing : undefined}
      />
    </>
  )
}
